import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onDateChange: (start: string, end: string) => void;
  disabled?: boolean;
}

export default function DateRangePicker({ startDate, endDate, onDateChange, disabled }: DateRangePickerProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setError(null);
  }, [startDate, endDate]);

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      setTempStartDate(e.target.value);
      setError(null);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      setTempEndDate(e.target.value);
      setError(null);
    }
  };

  const isValidDateRange = () => {
    const start = new Date(tempStartDate);
    const end = new Date(tempEndDate);
    return start <= end;
  };

  const handleApply = () => {
    if (!disabled && (tempStartDate !== startDate || tempEndDate !== endDate)) {
      if (!isValidDateRange()) {
        setError(t('dateTime.invalidDateRange'));
        return;
      }
      setError(null);
      onDateChange(tempStartDate, tempEndDate);
    }
  };

  return (
    <div className="relative inline-block">
      <div className="flex items-center space-x-2 md:space-x-2">
        <Calendar className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
        <input
          type="date"
          value={tempStartDate}
          onChange={handleStartDateChange}
          disabled={disabled}
          className={`text-sm border rounded-md p-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full md:w-auto ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700 text-white disabled:bg-gray-900 disabled:text-gray-500'
              : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100 disabled:text-gray-500'
          } ${error ? 'border-red-500' : ''}`}
        />
        <span className={`hidden md:inline ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{t('common.to')}</span>
        <input
          type="date"
          value={tempEndDate}
          onChange={handleEndDateChange}
          disabled={disabled}
          className={`text-sm border rounded-md p-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full md:w-auto ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700 text-white disabled:bg-gray-900 disabled:text-gray-500'
              : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100 disabled:text-gray-500'
          } ${error ? 'border-red-500' : ''}`}
        />
        <button
          onClick={handleApply}
          disabled={disabled || (tempStartDate === startDate && tempEndDate === endDate)}
          className={`px-3 py-1.5 text-sm font-medium rounded-md ${
            theme === 'dark'
              ? 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500'
              : 'bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-500'
          }`}
        >
          {t('common.apply')}
        </button>
      </div>
      {error && (
        <div className={`absolute left-0 top-full mt-1 text-sm ${theme === 'dark' ? 'text-red-400' : 'text-red-500'}`}>
          {error}
        </div>
      )}
    </div>
  );
}
